import { useEffect, useMemo, useState } from "react";
import {
  App as AntdApp,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ApartmentOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  HomeOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { contactRoleService } from "../../../services/api/contactRoleService";
import { formatPhone, phoneToTelHref } from "../../../utils/phone";
import type {
  ContactRole,
  ContactRoleTarget,
  ProducerContactChains,
  ReorderItem,
} from "../../../types/contact-role";

const { Text } = Typography;

export interface ProducerContactOption {
  id: string;          // ProducerContact uuid
  name: string;        // display name (Contact full name)
  position?: string | null;
  email?: string | null;
}

interface Props {
  producerId: number;
  /** Contacts already saved on the producer (to populate the "add" select). */
  availableContacts: ProducerContactOption[];
  /**
   * When true, renders panels as compact cards in a 3-per-row grid instead
   * of a vertical stack. Used by the dedicated chains page so users can see
   * License + multiple Farms at a glance.
   */
  grid?: boolean;
  /** When true the outer wrapping <Card> is omitted (the parent provides it). */
  bare?: boolean;
  /** Called when the user clicks "Editar" in the contact-info modal. The
   *  parent typically opens its own ProducerContact edit modal. */
  onEditProducerContact?: (producerContactId: string) => void;
  /** Called after a successful DELETE on a ProducerContact (soft-delete).
   *  Parent should re-fetch the producer so `availableContacts` updates. */
  onProducerContactDeleted?: () => void;
  /** Cuando es `false`, oculta todos los controles de edición
   *  (añadir/editar/reordenar/eliminar contactos). El componente
   *  queda en modo solo-lectura para ESA. Default: true. */
  canWrite?: boolean;
}

/**
 * Read-write panel for the producer's contact chains.
 *
 * Each chain (License + one per Farm) is rendered as a stack of priority
 * rows. Up/down arrows shift priority within the chain; the trash icon
 * removes the contact from that chain. The "add" select lists every
 * producer contact not yet present in the chain.
 *
 * Persistence is per-action (each click is its own API call). On every
 * mutation we re-fetch `chains` so the displayed priorities reflect the
 * server state, including any tie-breaks the backend applied.
 */
export const ContactChainsSection = ({
  producerId,
  availableContacts,
  grid = false,
  bare = false,
  onEditProducerContact,
  onProducerContactDeleted,
  canWrite = true,
}: Props) => {
  const { t } = useTranslation();
  const { message: notify } = AntdApp.useApp();

  const [chains, setChains] = useState<ProducerContactChains | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await contactRoleService.getChains(producerId);
      setChains(data);
    } catch (e) {
      console.error("contact-chains load:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!producerId) return;
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [producerId]);

  const addContact = async (
    target: ContactRoleTarget,
    farmId: number | null,
    producerContactId: string,
  ) => {
    const key = `add-${target}-${farmId ?? "x"}`;
    setBusyKey(key);
    try {
      // New contact gets the next priority slot at the end of the chain.
      const list = chainList(chains, target, farmId);
      const nextPriority = (list.length ? Math.max(...list.map((r) => r.priority)) : 0) + 1;
      await contactRoleService.create({
        producer_contact: producerContactId,
        target,
        farm: farmId,
        priority: nextPriority,
      });
      notify.success(t("contact_chains.saved"));
      await reload();
    } catch (e: any) {
      notify.error(t("contact_chains.save_error"));
    } finally {
      setBusyKey(null);
    }
  };

  const removeRole = async (role: ContactRole) => {
    setBusyKey(`del-${role.id}`);
    try {
      await contactRoleService.remove(role.id);
      notify.success(t("contact_chains.saved"));
      await reload();
    } catch {
      notify.error(t("contact_chains.save_error"));
    } finally {
      setBusyKey(null);
    }
  };

  const deleteProducerContact = async (producerContactId: string): Promise<boolean> => {
    setBusyKey(`pc-del-${producerContactId}`);
    try {
      const { apiClient } = await import("../../../services/api/apiClient");
      await apiClient.delete(`/producer-contacts/${producerContactId}/`);
      notify.success(
        t("contact_chains.contact_deleted", "Contacto eliminado del productor"),
      );
      await reload();
      onProducerContactDeleted?.();
      return true;
    } catch (e: any) {
      const detail =
        e?.response?.data?.error ||
        e?.response?.data?.detail ||
        t("contact_chains.delete_error", "No se pudo eliminar el contacto");
      notify.error(String(detail));
      return false;
    } finally {
      setBusyKey(null);
    }
  };

  const reorder = async (
    target: ContactRoleTarget,
    farmId: number | null,
    fromIdx: number,
    toIdx: number,
  ) => {
    if (!chains) return;
    const list = chainList(chains, target, farmId);
    if (toIdx < 0 || toIdx >= list.length || fromIdx === toIdx) return;
    const reordered = [...list];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    const items: ReorderItem[] = reordered.map((r, i) => ({
      id: r.id,
      priority: i + 1,
    }));
    setBusyKey(`reorder-${target}-${farmId ?? "x"}`);
    try {
      await contactRoleService.bulkReorder(target, farmId, items);
      await reload();
    } catch {
      notify.error(t("contact_chains.save_error"));
    } finally {
      setBusyKey(null);
    }
  };

  if (loading && !chains) {
    return bare ? <Spin /> : (
      <Card type="inner" title={t("contact_chains.title")}>
        <Spin />
      </Card>
    );
  }
  if (!chains) return null;

  // Build the panel list (License first, then a panel per farm). When
  // rendering as a grid we wrap each in a responsive Col; otherwise we
  // stack them vertically.
  const panels: React.ReactNode[] = [
    <ChainPanel
      key="license"
      icon={<SafetyCertificateOutlined />}
      accent="#722ED1"
      title={t("contact_chains.license")}
      contacts={chains.license}
      availableContacts={availableContacts}
      busyKey={busyKey}
      onAdd={(pcId) => addContact("license", null, pcId)}
      onRemove={removeRole}
      onMoveUp={(idx) => reorder("license", null, idx, idx - 1)}
      onMoveDown={(idx) => reorder("license", null, idx, idx + 1)}
      onEditProducerContact={onEditProducerContact}
      onDeleteProducerContact={deleteProducerContact}
      t={t}
    />,
    ...chains.farms.map((f) => (
      <ChainPanel
        key={`farm-${f.farm_id}`}
        icon={<HomeOutlined />}
        accent="#13C2C2"
        title={`${t("contact_chains.farm")}: ${f.farm_name}`}
        contacts={f.contacts}
        availableContacts={availableContacts}
        busyKey={busyKey}
        onAdd={(pcId) => addContact("farm", f.farm_id, pcId)}
        onRemove={removeRole}
        onMoveUp={(idx) => reorder("farm", f.farm_id, idx, idx - 1)}
        onMoveDown={(idx) => reorder("farm", f.farm_id, idx, idx + 1)}
        onEditProducerContact={onEditProducerContact}
        onDeleteProducerContact={deleteProducerContact}
        canWrite={canWrite}
        t={t}
      />
    )),
  ];

  const body = grid ? (
    <Row gutter={[16, 16]}>
      {panels.map((panel, idx) => (
        <Col xs={24} md={12} lg={8} key={(panel as any).key ?? idx}>
          {panel}
        </Col>
      ))}
    </Row>
  ) : (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {panels}
      {chains.farms.length === 0 && (
        <Empty
          description={
            <Text type="secondary" style={{ fontSize: 12 }}>
              <ApartmentOutlined /> {t("farms.title", "Fincas")} — el productor aún no tiene fincas
            </Text>
          }
        />
      )}
    </Space>
  );

  if (bare) return body;

  return (
    <Card
      type="inner"
      title={
        <span>
          <Text strong>{t("contact_chains.title")}</Text>
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
            {t("contact_chains.subtitle")}
          </Text>
        </span>
      }
    >
      {body}
    </Card>
  );
};

// -- helpers ----------------------------------------------------------------

function chainList(
  chains: ProducerContactChains | null,
  target: ContactRoleTarget,
  farmId: number | null,
): ContactRole[] {
  if (!chains) return [];
  if (target === "license") return chains.license;
  return chains.farms.find((f) => f.farm_id === farmId)?.contacts ?? [];
}

interface ChainPanelProps {
  icon: React.ReactNode;
  accent: string;
  title: string;
  contacts: ContactRole[];
  availableContacts: ProducerContactOption[];
  busyKey: string | null;
  onAdd: (producerContactId: string) => void;
  onRemove: (role: ContactRole) => void;
  onMoveUp: (idx: number) => void;
  onMoveDown: (idx: number) => void;
  onEditProducerContact?: (producerContactId: string) => void;
  onDeleteProducerContact?: (producerContactId: string) => Promise<boolean>;
  /** Cuando es `false`, oculta todos los controles de escritura. */
  canWrite?: boolean;
  t: (k: string, d?: any) => string;
}

const ChainPanel = ({
  icon,
  accent,
  title,
  contacts,
  availableContacts,
  busyKey,
  onAdd,
  onRemove,
  onMoveUp,
  onMoveDown,
  onEditProducerContact,
  onDeleteProducerContact,
  canWrite = true,
  t,
}: ChainPanelProps) => {
  const usedIds = useMemo(
    () => new Set(contacts.map((c) => c.producer_contact)),
    [contacts],
  );
  const selectableContacts = availableContacts.filter((pc) => !usedIds.has(pc.id));
  const [picked, setPicked] = useState<string | undefined>();

  const handleAddClick = () => {
    if (!picked) return;
    onAdd(picked);
    setPicked(undefined);
  };

  // Modal con toda la info del contacto. Se monta dentro del propio
  // panel para que herede el contexto de App (theme + tokens).
  const [infoContact, setInfoContact] = useState<ContactRole | null>(null);
  const closeInfo = () => setInfoContact(null);

  return (
    <div
      style={{
        border: `1px solid #f0f0f0`,
        borderRadius: 8,
        padding: 12,
        backgroundColor: "#fafafa",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 10,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: 8,
            backgroundColor: `${accent}1A`,
            color: accent,
            fontSize: 18,
          }}
        >
          {icon}
        </span>
        <div style={{ flex: 1 }}>
          <Text strong>{title}</Text>
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
            ({contacts.length})
          </Text>
        </div>
      </div>

      {contacts.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 13, fontStyle: "italic" }}>
          {t("contact_chains.no_contacts")}
        </Text>
      ) : (
        <ol style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
          {contacts.map((c, idx) => (
            <li
              key={c.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 10px",
                marginBottom: 6,
                borderRadius: 6,
                backgroundColor: "#fff",
                border: "1px solid #f0f0f0",
              }}
            >
              <Tag color={accent} style={{ minWidth: 36, textAlign: "center" }}>
                #{c.priority}
              </Tag>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>
                  {c.contact_name}
                  {c.position_name && (
                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
                      · {c.position_name}
                    </Text>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#595959",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "2px 12px",
                    marginTop: 2,
                  }}
                >
                  {c.contact_phone && (
                    <a
                      href={`tel:${phoneToTelHref(c.contact_phone)}`}
                      style={{ color: "#595959" }}
                    >
                      <PhoneOutlined style={{ marginRight: 4 }} />
                      {formatPhone(c.contact_phone)}
                    </a>
                  )}
                  {c.contact_email && (
                    <a
                      href={`mailto:${c.contact_email}`}
                      style={{ color: "#595959" }}
                    >
                      <MailOutlined style={{ marginRight: 4 }} />
                      {c.contact_email}
                    </a>
                  )}
                  {!c.contact_phone && !c.contact_email && (
                    <Text type="secondary" style={{ fontStyle: "italic" }}>
                      {t("contact_chains.no_contact_info", "Sin datos de contacto")}
                    </Text>
                  )}
                </div>
              </div>
              <Space size={4}>
                <Tooltip title={t("contact_chains.show_full", "Ver toda la información")}>
                  <Button
                    icon={<InfoCircleOutlined />}
                    size="small"
                    type="text"
                    onClick={() => setInfoContact(c)}
                  />
                </Tooltip>
                {canWrite && onEditProducerContact && (
                  <Tooltip title={t("contact_chains.edit_contact", "Editar datos del contacto")}>
                    <Button
                      icon={<EditOutlined />}
                      size="small"
                      type="text"
                      onClick={() => onEditProducerContact(c.producer_contact)}
                    />
                  </Tooltip>
                )}
                {canWrite && (
                  <>
                    <Tooltip title={t("contact_chains.move_up")}>
                      <Button
                        icon={<ArrowUpOutlined />}
                        size="small"
                        type="text"
                        disabled={idx === 0}
                        onClick={() => onMoveUp(idx)}
                      />
                    </Tooltip>
                    <Tooltip title={t("contact_chains.move_down")}>
                      <Button
                        icon={<ArrowDownOutlined />}
                        size="small"
                        type="text"
                        disabled={idx === contacts.length - 1}
                        onClick={() => onMoveDown(idx)}
                      />
                    </Tooltip>
                    <Popconfirm
                      title={t("contact_chains.remove_confirm")}
                      okText={t("common.confirm")}
                      cancelText={t("common.cancel")}
                      onConfirm={() => onRemove(c)}
                    >
                      <Tooltip title={t("contact_chains.remove")}>
                        <Button
                          icon={<DeleteOutlined />}
                          size="small"
                          type="text"
                          danger
                          loading={busyKey === `del-${c.id}`}
                        />
                      </Tooltip>
                    </Popconfirm>
                  </>
                )}
              </Space>
            </li>
          ))}
        </ol>
      )}

      <Modal
        open={!!infoContact}
        onCancel={closeInfo}
        footer={
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            {canWrite ? (
              <Popconfirm
              title={t(
                "contact_chains.delete_pc_confirm_title",
                "Eliminar contacto del productor",
              )}
              description={t(
                "contact_chains.delete_pc_confirm_body",
                "El contacto desaparecerá de todas las cadenas del productor. La persona seguirá existiendo y podrá volver a vincularse luego.",
              )}
              okText={t("common.delete", "Eliminar")}
              cancelText={t("common.cancel", "Cancelar")}
              okButtonProps={{ danger: true }}
              onConfirm={async () => {
                if (!infoContact || !onDeleteProducerContact) return;
                const ok = await onDeleteProducerContact(
                  infoContact.producer_contact,
                );
                if (ok) closeInfo();
              }}
              disabled={!onDeleteProducerContact}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={!onDeleteProducerContact}
                loading={
                  !!infoContact &&
                  busyKey === `pc-del-${infoContact.producer_contact}`
                }
              >
                {t("contact_chains.delete_pc", "Eliminar contacto")}
              </Button>
            </Popconfirm>
            ) : (
              <span />
            )}
            <Space>
              {canWrite && onEditProducerContact && (
                <Button
                  icon={<EditOutlined />}
                  onClick={() => {
                    if (!infoContact) return;
                    onEditProducerContact(infoContact.producer_contact);
                    closeInfo();
                  }}
                >
                  {t("common.edit", "Editar")}
                </Button>
              )}
              <Button onClick={closeInfo}>
                {t("common.close", "Cerrar")}
              </Button>
            </Space>
          </Space>
        }
        title={
          <Space>
            <InfoCircleOutlined style={{ color: accent }} />
            {infoContact?.contact_name}
            {infoContact?.position_name && (
              <Text type="secondary" style={{ fontSize: 13 }}>
                · {infoContact.position_name}
              </Text>
            )}
          </Space>
        }
        width={520}
      >
        {infoContact && (
          <Descriptions
            column={1}
            size="small"
            labelStyle={{ width: 130, color: "#8c8c8c" }}
          >
            <Descriptions.Item label={t("contacts.field.main_phone", "Teléfono principal")}>
              {infoContact.contact_phone ? (
                <a href={`tel:${phoneToTelHref(infoContact.contact_phone)}`}>
                  <PhoneOutlined style={{ marginRight: 6 }} />
                  {formatPhone(infoContact.contact_phone)}
                </a>
              ) : (
                <Text type="secondary">—</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t("contacts.field.secondary_phone", "Teléfono secundario")}>
              {infoContact.contact_secondary_phone ? (
                <a href={`tel:${phoneToTelHref(infoContact.contact_secondary_phone)}`}>
                  <PhoneOutlined style={{ marginRight: 6 }} />
                  {formatPhone(infoContact.contact_secondary_phone)}
                </a>
              ) : (
                <Text type="secondary">—</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t("contacts.field.email", "Email")}>
              {infoContact.contact_email ? (
                <a href={`mailto:${infoContact.contact_email}`}>
                  <MailOutlined style={{ marginRight: 6 }} />
                  {infoContact.contact_email}
                </a>
              ) : (
                <Text type="secondary">—</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t("contacts.field.identification_number", "Número de identificación")}>
              {infoContact.contact_nif ? (
                <>
                  <IdcardOutlined style={{ marginRight: 6 }} />
                  {infoContact.contact_nif}
                </>
              ) : (
                <Text type="secondary">—</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t("contacts.field.position", "Cargo")}>
              {infoContact.position_name || <Text type="secondary">—</Text>}
            </Descriptions.Item>
            {infoContact.contact_notes && (
              <Descriptions.Item label={t("contacts.field.notes", "Notas")}>
                <Text style={{ whiteSpace: "pre-wrap" }}>
                  {infoContact.contact_notes}
                </Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {!canWrite ? null : selectableContacts.length === 0 ? (
        // No more contacts to assign: replace the picker row with a small
        // hint so the card never overflows on narrow grid columns.
        <Text
          type="secondary"
          style={{
            fontSize: 12,
            marginTop: 10,
            display: "block",
            fontStyle: "italic",
          }}
        >
          {t("contact_chains.no_available_contacts")}
        </Text>
      ) : (
        <div
          style={{
            display: "flex",
            gap: 8,
            marginTop: 10,
            flexWrap: "wrap",
            // `min-width: 0` lets the inner Select shrink below its content
            // width inside the flex column, so the Add button never spills
            // out of the chain card.
            minWidth: 0,
          }}
        >
          <Select
            placeholder={t("contact_chains.select_contact")}
            value={picked}
            onChange={setPicked}
            options={selectableContacts.map((pc) => ({
              value: pc.id,
              label: pc.position
                ? `${pc.name} — ${pc.position}`
                : pc.name,
            }))}
            style={{ flex: 1, minWidth: 0 }}
            allowClear
            showSearch
            optionFilterProp="label"
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddClick}
            disabled={!picked}
          >
            {t("contact_chains.add_contact")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ContactChainsSection;
